<template>
  <aside v-if="visible" :class="['music-player', positionClass]">
    <button class="music-cover" type="button" @click="toggle">
      <img v-if="current.coverUrl" :src="current.coverUrl" :alt="current.title">
      <span v-else>♪</span>
    </button>
    <div class="music-main">
      <div class="music-title">{{ current.title }}</div>
      <div class="music-artist">{{ current.artist || 'Unknown' }}</div>
      <div class="music-actions">
        <button type="button" @click="prev">‹</button>
        <button type="button" :disabled="!current.audioUrl" @click="toggle">{{ playing ? '暂停' : '播放' }}</button>
        <button type="button" @click="next">›</button>
      </div>
      <audio ref="audio" :src="current.audioUrl" :autoplay="autoplay" @ended="next"></audio>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'MusicPlayer',
  props: {
    music: {
      type: Object,
      default: () => ({ config: {}, tracks: [] })
    }
  },
  data() {
    return {
      index: 0,
      playing: false,
      loading: false,
      loadError: '',
      remoteTracks: [],
      loadedMetingKey: ''
    };
  },
  computed: {
    config() {
      return this.music && this.music.config ? this.music.config : {};
    },
    tracks() {
      if (this.isMetingMode) {
        return this.remoteTracks;
      }
      return this.music && Array.isArray(this.music.tracks) ? this.music.tracks : [];
    },
    isMetingMode() {
      return (this.config.mode || 'local') === 'meting';
    },
    metingReady() {
      return this.isMetingMode && this.config.metingApi && this.config.metingId;
    },
    visible() {
      return Number(this.config.enabled) === 1 && (this.tracks.length > 0 || this.loading || this.loadError || this.metingReady);
    },
    current() {
      return this.tracks[this.index] || {
        title: this.loading ? '歌单加载中' : '暂无音乐',
        artist: this.loadError || (this.isMetingMode ? '网易云歌单' : 'Unknown'),
        audioUrl: ''
      };
    },
    autoplay() {
      return Number(this.config.autoplay) === 1;
    },
    positionClass() {
      return this.config.playerPosition === 'left-bottom' ? 'left-bottom' : 'right-bottom';
    }
  },
  watch: {
    music: {
      immediate: true,
      deep: true,
      handler() {
        this.preparePlaylist();
      }
    },
    current() {
      this.syncAudio();
    }
  },
  mounted() {
    this.syncAudio();
  },
  methods: {
    preparePlaylist() {
      if (Number(this.config.enabled) !== 1) {
        this.remoteTracks = [];
        this.loadError = '';
        return;
      }
      if (!this.isMetingMode) {
        this.remoteTracks = [];
        this.loadError = '';
        this.loadedMetingKey = '';
        return;
      }
      this.loadMetingPlaylist();
    },
    loadMetingPlaylist() {
      if (!this.config.metingApi || !this.config.metingId) {
        this.remoteTracks = [];
        this.loadError = '请配置网易云歌单ID';
        return;
      }
      const key = [
        this.config.metingApi,
        this.config.metingServer || 'netease',
        this.config.metingType || 'playlist',
        this.config.metingId
      ].join('|');
      if (this.loadedMetingKey === key && this.remoteTracks.length > 0) {
        return;
      }
      this.loadedMetingKey = key;
      this.loading = true;
      this.loadError = '';
      fetch(this.buildMetingUrl())
        .then(res => {
          if (!res.ok) {
            throw new Error('meting api error');
          }
          return res.json();
        })
        .then(data => {
          const tracks = this.normalizeMetingList(data);
          this.remoteTracks = tracks.filter(track => track.audioUrl);
          this.index = 0;
          if (!this.remoteTracks.length) {
            this.loadError = '歌单暂无可播放歌曲';
          }
          this.syncAudio();
        })
        .catch(() => {
          this.remoteTracks = [];
          this.loadError = '歌单获取失败';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildMetingUrl() {
      return this.config.metingApi
        .replace(':server', this.config.metingServer || 'netease')
        .replace(':type', this.config.metingType || 'playlist')
        .replace(':id', this.config.metingId)
        .replace(':auth', '')
        .replace(':r', Date.now().toString());
    },
    normalizeMetingList(data) {
      const list = Array.isArray(data) ? data : (data && (data.data || data.list || data.playlist)) || [];
      if (!Array.isArray(list)) {
        return [];
      }
      return list.map(song => ({
        id: song.id,
        title: song.name || song.title || '未知歌曲',
        artist: song.artist || song.author || this.joinArtists(song.artists) || 'Unknown',
        album: song.album || '',
        coverUrl: song.pic || song.cover || song.coverUrl || song.picUrl || '',
        audioUrl: song.url || song.audioUrl || '',
        lyricUrl: song.lrc || song.lyric || song.lyricUrl || ''
      }));
    },
    joinArtists(artists) {
      if (!Array.isArray(artists)) {
        return '';
      }
      return artists.map(item => item && (item.name || item)).filter(Boolean).join(' / ');
    },
    syncAudio() {
      this.$nextTick(() => {
        const audio = this.$refs.audio;
        if (!audio) {
          return;
        }
        audio.volume = (Number(this.config.volume) || 60) / 100;
        if ((this.playing || this.autoplay) && this.current.audioUrl) {
          audio.play().then(() => {
            this.playing = true;
          }).catch(() => {
            this.playing = false;
          });
        }
      });
    },
    toggle() {
      const audio = this.$refs.audio;
      if (!audio || !this.current.audioUrl) {
        return;
      }
      if (this.playing) {
        audio.pause();
        this.playing = false;
      } else {
        audio.play().then(() => {
          this.playing = true;
        }).catch(() => {
          this.playing = false;
        });
      }
    },
    prev() {
      if (!this.tracks.length) {
        return;
      }
      this.index = this.index === 0 ? this.tracks.length - 1 : this.index - 1;
    },
    next() {
      if (!this.tracks.length) {
        return;
      }
      if (this.config.loopMode === 'single') {
        this.$refs.audio.currentTime = 0;
        this.$refs.audio.play().catch(() => {
          this.playing = false;
        });
        return;
      }
      if (this.config.loopMode === 'random') {
        this.index = Math.floor(Math.random() * this.tracks.length);
        return;
      }
      this.index = (this.index + 1) % this.tracks.length;
    }
  }
};
</script>
